import { BoxProps, FormControl } from '@chakra-ui/react'
import { InputValueDataTypeConfig } from '@helo/cms'
import { Deferred } from '@helo/utils'
import React, { useMemo, useRef, useState } from 'react'

import { SelectInput } from '../../inputs/SelectInput/SelectInput'
import { ConditionOption } from '../StringFilterEditor/StringFilterEditor'

export type BooleanFilterEditorProps = BoxProps & {
  inputValueConfig: InputValueDataTypeConfig
  conditionOptions: ConditionOption[]
  selectedField?: ConditionOption | undefined
  updateSelectedField?: (field: ConditionOption | undefined) => void
  selectedFieldRef?: React.MutableRefObject<ConditionOption | undefined>
  onUpdate?: (config: InputValueDataTypeConfig) => void
  autoFocus?: boolean
  isDisabled?: boolean
  /** Called after a condition is selected, so parent can advance focus to the value input */
  onConditionSelected?: () => void
  /** When true, Enter with condition menu open stops propagation so parent modal does not also handle Enter. */
  stopEnterPropagationWhenMenuOpen?: boolean
  /** When false, select menus stay closed (e.g. parent popover closed). */
  isFilterEditorOpen?: boolean
}

const booleanOptions = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
]

export const BooleanFilterEditor = ({
  inputValueConfig,
  conditionOptions,
  selectedField: selectedFieldProp,
  updateSelectedField: updateSelectedFieldProp,
  selectedFieldRef: selectedFieldRefProp,
  onUpdate = () => {},
  autoFocus,
  isDisabled = false,
  onConditionSelected,
  stopEnterPropagationWhenMenuOpen,
  isFilterEditorOpen = true,
  ...props
}: BooleanFilterEditorProps) => {
  // Use props if provided (from FilterEditorRow), otherwise manage locally
  const [localSelectedField, setLocalSelectedField] = useState<
    ConditionOption | undefined
  >(undefined)
  const localSelectedFieldRef = useRef<ConditionOption | undefined>(undefined)

  const selectedField = selectedFieldProp ?? localSelectedField
  const selectedFieldRef = selectedFieldRefProp ?? localSelectedFieldRef
  const updateSelectedField =
    updateSelectedFieldProp ??
    ((field: ConditionOption | undefined) => {
      setLocalSelectedField(field)
      localSelectedFieldRef.current = field
    })

  const currentValue = inputValueConfig.state.asValue as
    | Record<string, any>
    | undefined

  // Get the current boolean value
  const getCurrentBooleanValue = () => {
    if (!currentValue || !selectedField) return undefined

    if (currentValue[selectedField.value] !== undefined) {
      return booleanOptions.find(
        opt => opt.value === String(currentValue[selectedField.value]),
      )
    }

    if (
      selectedField.value === 'equals' &&
      currentValue.not?.equals !== undefined
    ) {
      return booleanOptions.find(
        opt => opt.value === String(currentValue.not.equals),
      )
    }

    return undefined
  }

  const [selectedBooleanValue, setSelectedBooleanValue] = useState<
    { label: string; value: string } | undefined
  >(getCurrentBooleanValue())

  const handleBooleanChange = (option: any) => {
    setSelectedBooleanValue(option)

    // Use the current selectedField from ref to avoid closure issues
    const currentSelectedField = selectedFieldRef.current
    if (!currentSelectedField || !option) return

    const boolValue =
      option.value === 'true'
        ? true
        : option.value === 'false'
          ? false
          : undefined
    if (boolValue === undefined) return

    const newValue: Record<string, any> = {}

    if (currentSelectedField.value === 'equals') {
      newValue.equals = boolValue
    } else {
      newValue[currentSelectedField.value] = boolValue
    }

    const updatedConfig = {
      ...inputValueConfig,
      state: Deferred.merge({ asValue: newValue })(
        inputValueConfig.state ?? {},
      ),
    }
    onUpdate(updatedConfig)
  }

  return (
    <>
      <SelectInput
        variant="filterCondition"
        options={conditionOptions}
        placeholder="Search or select condition"
        isDisabled={isDisabled}
        filterMode="substring"
        stopEnterPropagationWhenMenuOpen={stopEnterPropagationWhenMenuOpen}
        menuIsOpen={isFilterEditorOpen ? undefined : false}
        onChange={(option: any) => {
          const selected = conditionOptions.find(
            opt => opt.value === option.value,
          )
          if (selected) {
            updateSelectedField(selected)
            onConditionSelected?.()
          } else {
            updateSelectedField(undefined)
          }
          setSelectedBooleanValue(undefined)
        }}
        value={selectedField}
        flex="1"
      />
      {conditionOptions.length > 0 && (
        <FormControl flex="1" isDisabled={isDisabled}>
          <SelectInput
            options={booleanOptions}
            placeholder="Select value"
            isDisabled={isDisabled}
            onChange={handleBooleanChange}
            value={selectedBooleanValue}
            width="100%"
            filterMode="substring"
            menuIsOpen={isFilterEditorOpen ? undefined : false}
          />
        </FormControl>
      )}
    </>
  )
}

BooleanFilterEditor.displayName = 'BooleanFilterEditor'
